// src/features/websites/components/ContentCollectionTab.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Article as ArticleIcon,
  Code as CodeIcon,
  Computer as ComputerIcon,
  RssFeed as RssIcon,
  VpnKey as ApiKeyIcon,
  Email as EmailIcon,
  InfoOutlined
} from '@mui/icons-material';
import ApiKeyConfigFields from './ApiKeyConfigFields';
import DomSelectorFields from './DomSelectorFields';
import { HeadlessConfigFields } from './HeadlessConfigFields';
import { RssConfigFields } from './RssConfigFields';
import { EmailConfigFields } from './EmailConfigFields';
import HeadlinesUrlField from './HeadlinesUrlField';
import { collectNow } from '../api';
import { TimeIcon } from '@mui/x-date-pickers';
import { useDispatch } from 'react-redux';
import { fetchFilteredArticles } from '../../articles/slices/articlesSlice';
import { AppDispatch } from '../../../store';
import { useTheme, alpha } from '@mui/material/styles';

// Define types for frequency options
interface FrequencyOption {
  label: string;
  value: number;
}

interface ContentCollectionTabProps {
  formData: {
    websitePK: string;
    url: string;
    status?: string;
    scrapingStrategy?: 'dom' | 'headless' | 'rss' | 'apikey' | 'email';
    scrapingFrequencyHours?: number;
    headlinesUrl?: string;
    selectors?: {
      articleContainer?: string;
      headline?: string;
      subheadline?: string;
      content?: string;
      publishDate?: string;
      author?: string;
      image?: string;
      category?: string;
      fullArticleContent?: string;
      fullArticleImage?: string;
    };
    rssUrl?: string;
    headlessAuth?: {
      username?: string;
      password?: string;
    };
    apiKey?: string;
    emailAddress?: string;
  };
  errors: Record<string, string>;
  isLoading: boolean;
  onStrategyChange: (event: SelectChangeEvent<string>) => void;
  onFrequencyChange: (event: SelectChangeEvent<number>) => void;
  onSelectorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectorsChange?: (selectors: Record<string, string>) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onHeadlessAuthChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStatusChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

// Pre-defined frequency options
const frequencyOptions: FrequencyOption[] = [
  { label: 'Manually', value: 0 },
  { label: 'Every hour', value: 1 },
  { label: 'Every 3 hours', value: 3 },
  { label: 'Every 6 hours', value: 6 },
  { label: 'Twice daily', value: 12 },
  { label: 'Daily', value: 24 },
  { label: 'Every other day', value: 48 },
  { label: 'Weekly', value: 168 },
];

const ContentCollectionTab: React.FC<ContentCollectionTabProps> = ({
  formData,
  errors,
  isLoading,
  onStrategyChange,
  onFrequencyChange,
  onSelectorChange,
  onSelectorsChange,
  onInputChange,
  onHeadlessAuthChange,
  onStatusChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();

  const [isCollecting, setIsCollecting] = useState(false);
  const [collectionType, setCollectionType] = useState<'incremental' | 'full'>('incremental');
  const [collectionResults, setCollectionResults] = useState<null | {
    message: string;
    duration: number;
    count: number;
    websiteId: string;
  }>(null);
  const [showResultsDialog, setShowResultsDialog] = useState(false);

  const handleCollectionTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCollectionType(event.target.value as 'incremental' | 'full');
  };

  const scrapeNow = async () => {
    try {
      setIsCollecting(true);
      setCollectionResults(null);

      const results = await collectNow(formData.websitePK, collectionType);
      setCollectionResults(results);
      setShowResultsDialog(true);

      // Refresh articles after collection - pass an empty filter object
      dispatch(fetchFilteredArticles({}));
    } catch (error) {
      console.error('Error collecting content:', error);
      alert('Error collecting content. Please try again.');
    } finally {
      setIsCollecting(false);
    }
  };

  const handleCloseDialog = () => {
    setShowResultsDialog(false);
  };

  const handleStrategyRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const mockSelectEvent = {
      target: { value: event.target.value }
    } as SelectChangeEvent<string>;
    onStrategyChange(mockSelectEvent);
  };

  return (
    <Grid container spacing={3}>
      {/* Header Section with Collection Strategy and Controls */}
      <Grid item xs={12}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          {/* Info and Collection Enabled Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <InfoOutlined sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Configure how Location Station collects content from this news source.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.status === 'ACTIVE'}
                  onChange={onStatusChange}
                  name="status"
                  disabled={isLoading}
                />
              }
              label={
                <Typography variant="body1" fontWeight="medium">
                  Collection Enabled
                </Typography>
              }
              sx={{ ml: 2 }}
            />
          </Box>

          {/* Collection Strategy Radio Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Collection Strategy
            </Typography>
            <RadioGroup
              row
              value={formData.scrapingStrategy || 'dom'}
              onChange={handleStrategyRadioChange}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                '& .MuiFormControlLabel-root': {
                  flex: 1,
                  margin: 0,
                  mr: 2,
                  '&:last-child': { mr: 0 }
                }
              }}
            >
              <FormControlLabel
                value="dom"
                control={<Radio disabled={isLoading} />}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 80 }}>
                    <CodeIcon sx={{ fontSize: 32, mb: 1, color: formData.scrapingStrategy === 'dom' ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" align="center" fontWeight="medium">
                      Standard HTML
                    </Typography>
                    <Typography variant="caption" align="center" color="text.secondary">
                      Direct HTML content
                    </Typography>
                  </Box>
                }
                sx={{
                  border: formData.scrapingStrategy === 'dom' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: formData.scrapingStrategy === 'dom' ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12) : theme.palette.background.paper,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
              />
              <FormControlLabel
                value="headless"
                control={<Radio disabled={isLoading} />}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 80 }}>
                    <ComputerIcon sx={{ fontSize: 32, mb: 1, color: formData.scrapingStrategy === 'headless' ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" align="center" fontWeight="medium">
                      Interactive Website
                    </Typography>
                    <Typography variant="caption" align="center" color="text.secondary">
                      JavaScript required
                    </Typography>
                  </Box>
                }
                sx={{
                  border: formData.scrapingStrategy === 'headless' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: formData.scrapingStrategy === 'headless' ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12) : theme.palette.background.paper,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
              />
              <FormControlLabel
                value="rss"
                control={<Radio disabled={isLoading} />}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 80 }}>
                    <RssIcon sx={{ fontSize: 32, mb: 1, color: formData.scrapingStrategy === 'rss' ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" align="center" fontWeight="medium">
                      RSS Feed
                    </Typography>
                    <Typography variant="caption" align="center" color="text.secondary">
                      RSS/XML feed
                    </Typography>
                  </Box>
                }
                sx={{
                  border: formData.scrapingStrategy === 'rss' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: formData.scrapingStrategy === 'rss' ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12) : theme.palette.background.paper,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
              />
              <FormControlLabel
                value="apikey"
                control={<Radio disabled={isLoading} />}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 80 }}>
                    <ApiKeyIcon sx={{ fontSize: 32, mb: 1, color: formData.scrapingStrategy === 'apikey' ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" align="center" fontWeight="medium">
                      CMS Integration
                    </Typography>
                    <Typography variant="caption" align="center" color="text.secondary">
                      API key access
                    </Typography>
                  </Box>
                }
                sx={{
                  border: formData.scrapingStrategy === 'apikey' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: formData.scrapingStrategy === 'apikey' ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12) : theme.palette.background.paper,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
              />
              <FormControlLabel
                value="email"
                control={<Radio disabled={isLoading} />}
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, minHeight: 80 }}>
                    <EmailIcon sx={{ fontSize: 32, mb: 1, color: formData.scrapingStrategy === 'email' ? 'primary.main' : 'text.secondary' }} />
                    <Typography variant="body2" align="center" fontWeight="medium">
                      Email Collection
                    </Typography>
                    <Typography variant="caption" align="center" color="text.secondary">
                      Email-based news
                    </Typography>
                  </Box>
                }
                sx={{
                  border: formData.scrapingStrategy === 'email' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: formData.scrapingStrategy === 'email' ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.12) : theme.palette.background.paper,
                  '&:hover': { backgroundColor: theme.palette.action.hover }
                }}
              />
            </RadioGroup>

            {/* Strategy Description */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              {formData.scrapingStrategy === 'dom' && "Use for standard websites where content is directly available in the HTML"}
              {formData.scrapingStrategy === 'headless' && "Use for websites that require JavaScript or interaction to load content"}
              {formData.scrapingStrategy === 'rss' && "Use if the website provides an RSS feed for its content"}
              {formData.scrapingStrategy === 'apikey' && "Use to integrate directly with your CMS and avoid scraping entirely"}
              {formData.scrapingStrategy === 'email' && "Use to collect news content delivered via email"}
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Render fields specific to the selected strategy */}
      {formData.scrapingStrategy === 'dom' && (
        <>

          {/* Collection Controls */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Collection Type Radio Group */}
                <FormControl component="fieldset" sx={{ mr: 3 }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                    Collection Type
                  </Typography>
                  <RadioGroup
                    row
                    name="collection-type"
                    value={collectionType}
                    onChange={handleCollectionTypeChange}
                  >
                    <FormControlLabel
                      value="incremental"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2">Incremental</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Only new articles
                          </Typography>
                        </Box>
                      }
                      disabled={isLoading || isCollecting}
                    />
                    <FormControlLabel
                      value="full"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2">Full</Typography>
                          <Typography variant="caption" color="text.secondary">
                            All available articles
                          </Typography>
                        </Box>
                      }
                      disabled={isLoading || isCollecting}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Collect Now Button */}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formData.status !== 'ACTIVE' || isLoading || isCollecting}
                  onClick={scrapeNow}
                  startIcon={isCollecting ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{ minWidth: 140 }}
                >
                  {isCollecting ? 'Collecting...' : 'Collect Now'}
                </Button>
              </Box>
            </Box>
          </Grid>
          {/* Results Dialog */}
          <Dialog
            open={showResultsDialog}
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Collection Results
            </DialogTitle>
            <DialogContent>
              {collectionResults && (
                <Paper elevation={0} sx={{ p: 2, backgroundColor: theme.palette.action.hover }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    {collectionResults.message}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Chip
                      icon={<ArticleIcon />}
                      label={`${collectionResults.count} Articles`}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<TimeIcon />}
                      label={`${collectionResults.duration.toFixed(2)} seconds`}
                      color="secondary"
                      variant="outlined"
                    />
                    <Chip
                      icon={<CheckIcon />}
                      label={`Website ID: ${collectionResults.websiteId}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`Type: ${collectionType === 'incremental' ? 'Incremental' : 'Full'}`}
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                </Paper>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Headlines URL Field - Required for all collection strategies */}
          <Grid item xs={12}>
            <HeadlinesUrlField
              headlinesUrl={formData.headlinesUrl || ''}
              websiteUrl={formData.url}
              error={errors['headlinesUrl']}
              isLoading={isLoading}
              onChange={onInputChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel id="frequency-label">Collection Frequency</InputLabel>
              <Select
                labelId="frequency-label"
                id="frequency"
                value={formData.scrapingFrequencyHours || 6}
                label="Collection Frequency *"
                onChange={onFrequencyChange}
                disabled={isLoading}
              >
                {frequencyOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                How often Location Station should check for new content
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <DomSelectorFields
              websitePK={formData.websitePK || ''}
              selectors={formData.selectors || {}}
              websiteUrl={formData.headlinesUrl || ''}
              headlinesUrl={formData.headlinesUrl || ''}
              errors={errors}
              isLoading={isLoading}
              onChange={onSelectorChange}
              onSelectorsChange={onSelectorsChange}
            />
          </Grid>
        </>
      )}

      {formData.scrapingStrategy === 'rss' && (
        <Grid item xs={12}>
          <RssConfigFields
            websitePK={formData.websitePK || ''}
            rssUrl={formData.rssUrl || ''}
            error={errors['rssUrl']}
            isLoading={isLoading}
            onChange={onInputChange}
          />
        </Grid>
      )}

      {formData.scrapingStrategy === 'headless' && (
        <Grid item xs={12}>
          <HeadlessConfigFields
            username={formData.headlessAuth?.username || ''}
            password={formData.headlessAuth?.password || ''}
            isLoading={isLoading}
            onChange={onHeadlessAuthChange}
          />
        </Grid>
      )}

      {formData.scrapingStrategy === 'apikey' && (
        <Grid item xs={12}>
          <ApiKeyConfigFields
            apiKey={formData.apiKey || ''}
            isLoading={isLoading}
            onChange={onInputChange}
          />
        </Grid>
      )}

      {formData.scrapingStrategy === 'email' && (
        <Grid item xs={12}>
          <EmailConfigFields
            incomingEmailAddress={formData.emailAddress || ''}
            isLoading={isLoading}
            onChange={onInputChange}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ContentCollectionTab;